import { FormInstance } from 'antd';
import { AxiosError } from 'axios';
import { useCallback, useMemo, useState } from 'react';

import { RoleR, rolesService } from '../../services/roles';
import { UserC, UserR, usersService, UserU } from '../../services/users';
import { FormValues } from '../../types/types';
import { openSuccessNotification } from '../../utils/notifications';
import { showApiErrors } from '../../utils/showApiErrors';
import { transformRoleValueToRoleData, createInitialRolesValue } from '../../utils/userFormUtils';

interface UseUserFormProps {
  user?: UserR;
  onSuccess: () => void;
  form: FormInstance<FormValues>;
}

export type UseUserFormReturn = {
  wasFormChanged: boolean;
  roles: RoleR[];
  loading: boolean;
  loadingRoles: boolean;
  titleAndBtnText: string;
  initialValues: Partial<FormValues> | undefined;
  handleFormValuesChange: () => void;
  resetForm: () => void;
  loadRoles: () => Promise<void>;
  handleSubmit: (values: FormValues) => Promise<void>;
};

export const useUserForm = ({ user, onSuccess, form }: UseUserFormProps): UseUserFormReturn => {
  const [wasFormChanged, setWasFormChanged] = useState(false);
  const [roles, setRoles] = useState<RoleR[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const titleAndBtnText = `${user ? 'Edit' : 'Add'} User`;

  // Memoized initial values
  const initialValues = useMemo((): Partial<FormValues> | undefined => {
    if (!user) {
      return undefined;
    }

    return {
      ...user,
      avatar: user.avatar ? user.avatar : null,
      allowedRedirectDomains: user.allowedRedirectDomains ?? undefined,
      // Create role-condition combination values
      roles: createInitialRolesValue(user)
    };
  }, [user]);

  const handleFormValuesChange = useCallback(() => {
    setWasFormChanged(true);
  }, []);

  const resetForm = useCallback(() => {
    form.resetFields();
    setWasFormChanged(false);
  }, [form]);

  const loadRoles = useCallback(async () => {
    try {
      setLoadingRoles(true);
      const data = await rolesService.getAll();
      setRoles(data);
    } catch (e) {
      showApiErrors(e as AxiosError);
    } finally {
      setLoadingRoles(false);
    }
  }, []);

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        setLoading(true);

        // Transform role-condition combination values back to roles format
        const { roles: formRoles = [], ...otherValues } = values;

        const transformedValues = {
          ...otherValues,
          roles: formRoles.map((roleValue: string) => transformRoleValueToRoleData(roleValue))
        } as UserC | UserU;

        if (user) {
          await usersService.edit(user.id, transformedValues);
          openSuccessNotification({ message: 'User successfully edited!' });
        } else {
          await usersService.add(transformedValues);
          openSuccessNotification({ message: 'User successfully added!' });
        }

        onSuccess();
      } catch (e) {
        showApiErrors(e as AxiosError);
      } finally {
        setLoading(false);
      }
    },
    [user, onSuccess]
  );

  return {
    wasFormChanged,
    roles,
    loading,
    loadingRoles,
    titleAndBtnText,
    initialValues,
    handleFormValuesChange,
    resetForm,
    loadRoles,
    handleSubmit
  };
};
